import React, { useMemo, useEffect, useState } from 'react';
import { useGetChartSvgQuery } from 'redux/apiSlice/reporting.slice';
import { Spin, Alert } from 'antd';
import { StyledSvgContainer } from './ChartImage.styles';

interface ChartImageProps {
  id: string;
  filename: string;
  onFileLoaded: (svg: string) => void;
  alt: string;
}

export const ChartImage: React.FC<ChartImageProps> = ({
  id,
  filename,
  onFileLoaded,
  alt,
}) => {
  const path = useMemo(() => `${id}/${filename}`, [id, filename]);
  const [responsiveSvg, setResponsiveSvg] = useState<string>('');
  const { data: svg, isLoading, error } = useGetChartSvgQuery({ path });

  useEffect(() => {
    if (svg) {
      // Extract viewBox if it exists, or create one from width and height
      let viewBox = svg.match(/viewBox="([^"]+)"/)?.[1];

      if (!viewBox) {
        const width = svg.match(/width="(\d+)"/)?.[1];
        const height = svg.match(/height="(\d+)"/)?.[1];
        if (width && height) {
          viewBox = `0 0 ${width} ${height}`;
        }
      }

      // Remove width, height, and existing viewBox attributes
      let modifiedSvg = svg
        .replace(/width="[^"]*"/, '')
        .replace(/height="[^"]*"/, '')
        .replace(/viewBox="[^"]*"/, '');

      // Add the viewBox attribute
      if (viewBox) {
        modifiedSvg = modifiedSvg.replace('<svg', `<svg viewBox="${viewBox}"`);
      }

      setResponsiveSvg(modifiedSvg);
      onFileLoaded(modifiedSvg);
    }
  }, [svg, onFileLoaded]);

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (error) {
    return <Alert message={`Error loading chart: ${error}`} type="error" />;
  }

  if (!svg) {
    return <Alert message="No chart data available" type="warning" />;
  }

  return (
    <StyledSvgContainer
      dangerouslySetInnerHTML={{ __html: responsiveSvg }}
      aria-label={alt}
    />
  );
};

export default ChartImage;
